import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import FormikTextField from 'components/Fields/FormikTextField'
import { Form, Formik, FormikHelpers } from 'formik'
import React from 'react'
import { apiStatus } from 'shared/constants/AppEnum'
import { IApiState } from 'store/types'
import {
    IOfferingResponse,
    StaffProfileResponseType,
    StaffPujaAssignmentPayloadType,
    StaffPujaAssignmentResponseType,
} from 'types/api/temple'
import { extractError, isFieldError } from 'utils/api'
import * as yup from 'yup'

interface FormStaffAssignmentProps {
    initialValues?: StaffPujaAssignmentResponseType
    priests: StaffProfileResponseType[]
    offerings: IOfferingResponse[]
    buttonLabel: string
    api: IApiState
    onSubmit: (values: StaffPujaAssignmentPayloadType, helpers: FormikHelpers<StaffPujaAssignmentPayloadType>) => void | Promise<any>
}

const initialFormValues = (item?: StaffPujaAssignmentResponseType): StaffPujaAssignmentPayloadType => ({
    staffProfile: item?.staffProfile ?? '',
    offering: item?.offering ?? '',
    venue: item?.venue ?? null,
    startDate: item?.startDate ?? '',
    endDate: item?.endDate ?? '',
    startTime: item?.startTime ?? '',
    endTime: item?.endTime ?? '',
    recurrence: item?.recurrence ?? 'none',
    recurrenceInterval: item?.recurrenceInterval ?? 1,
    displayInHomePage: item?.displayInHomePage ?? false,
    displayInTempleCalendar: item?.displayInTempleCalendar ?? true,
    annualPuja: item?.annualPuja ?? false,
    occurrenceCount: item?.occurrenceCount ?? null,
    notes: item?.notes ?? '',
    status: item?.status ?? 'scheduled',
})

const normalizePayload = (values: StaffPujaAssignmentPayloadType): StaffPujaAssignmentPayloadType => ({
    ...values,
    recurrenceInterval: Number(values.recurrenceInterval || 1),
    occurrenceCount: values.annualPuja && values.occurrenceCount ? Number(values.occurrenceCount) : null,
    venue: values.venue || null,
})

const FormStaffAssignment: React.FC<FormStaffAssignmentProps> = ({
    initialValues,
    priests,
    offerings,
    buttonLabel,
    api,
    onSubmit,
}) => {
    const validationSchema = yup.object({
        staffProfile: yup.string().required('Priest is required'),
        offering: yup.string().required('Offering is required'),
        startDate: yup.string().required('Start date is required'),
        endDate: yup.string().required('End date is required'),
        startTime: yup.string().required('Start time is required'),
        endTime: yup.string().required('End time is required'),
        recurrenceInterval: yup.number().min(1, 'Must be at least 1').required('Required'),
    })

    return (
        <Formik
            enableReinitialize
            initialValues={initialFormValues(initialValues)}
            validationSchema={validationSchema}
            onSubmit={(values, helpers) => onSubmit(normalizePayload(values), helpers)}
        >
            {({ dirty, errors, isSubmitting, isValid, setFieldValue, touched, values }) => (
                <Form noValidate autoComplete='off'>
                    <Grid container spacing={6} direction='column'>
                        {api.status === apiStatus.FAILED && !isFieldError(api.error) && (
                            <Grid item>
                                <Alert severity='error'>{extractError(api.error)}</Alert>
                            </Grid>
                        )}
                        <Grid item>
                            <FormControl variant='outlined' fullWidth required error={Boolean(touched.staffProfile && errors.staffProfile)}>
                                <InputLabel id='staff-assignment-priest-label'>Priest</InputLabel>
                                <Select
                                    labelId='staff-assignment-priest-label'
                                    label='Priest'
                                    value={values.staffProfile}
                                    onChange={event => setFieldValue('staffProfile', event.target.value)}
                                >
                                    {priests.map(profile => (
                                        <MenuItem key={profile.id} value={profile.id}>{profile.fullName}</MenuItem>
                                    ))}
                                </Select>
                                {touched.staffProfile && errors.staffProfile && <FormHelperText>{errors.staffProfile}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item>
                            <FormControl variant='outlined' fullWidth required error={Boolean(touched.offering && errors.offering)}>
                                <InputLabel id='staff-assignment-offering-label'>Offering</InputLabel>
                                <Select
                                    labelId='staff-assignment-offering-label'
                                    label='Offering'
                                    value={values.offering}
                                    onChange={event => setFieldValue('offering', event.target.value)}
                                >
                                    {offerings.map(offering => (
                                        <MenuItem key={offering.id} value={offering.id}>{offering.name}</MenuItem>
                                    ))}
                                </Select>
                                {touched.offering && errors.offering && <FormHelperText>{errors.offering}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item container spacing={6}>
                            <Grid item xs={12} md={6}>
                                <FormikTextField name='startDate' label='Start Date' type='date' variant='outlined' InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormikTextField name='endDate' label='End Date' type='date' variant='outlined' InputLabelProps={{ shrink: true }} />
                            </Grid>
                        </Grid>
                        <Grid item container spacing={6}>
                            <Grid item xs={12} md={6}>
                                <FormikTextField name='startTime' label='Start Time' type='time' variant='outlined' InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormikTextField name='endTime' label='End Time' type='time' variant='outlined' InputLabelProps={{ shrink: true }} />
                            </Grid>
                        </Grid>
                        <Grid item container spacing={6}>
                            <Grid item xs={12} md={6}>
                                <FormControl variant='outlined' fullWidth>
                                    <InputLabel id='staff-assignment-recurrence-label'>Recurrence</InputLabel>
                                    <Select
                                        labelId='staff-assignment-recurrence-label'
                                        label='Recurrence'
                                        value={values.recurrence}
                                        onChange={event => setFieldValue('recurrence', event.target.value)}
                                    >
                                        <MenuItem value='none'>None</MenuItem>
                                        <MenuItem value='daily'>Daily</MenuItem>
                                        <MenuItem value='weekly'>Weekly</MenuItem>
                                        <MenuItem value='monthly'>Monthly</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormikTextField name='recurrenceInterval' label='Recurrence Interval' type='number' variant='outlined' />
                            </Grid>
                        </Grid>
                        <Grid item container spacing={2}>
                            <Grid item>
                                <FormControlLabel
                                    control={<Checkbox color='primary' checked={values.displayInHomePage} onChange={event => setFieldValue('displayInHomePage', event.target.checked)} />}
                                    label='Display in home page'
                                />
                            </Grid>
                            <Grid item>
                                <FormControlLabel
                                    control={<Checkbox color='primary' checked={values.displayInTempleCalendar} onChange={event => setFieldValue('displayInTempleCalendar', event.target.checked)} />}
                                    label='Display in temple calendar'
                                />
                            </Grid>
                            <Grid item>
                                <FormControlLabel
                                    control={<Checkbox color='primary' checked={values.annualPuja} onChange={event => setFieldValue('annualPuja', event.target.checked)} />}
                                    label='Annual puja'
                                />
                            </Grid>
                        </Grid>
                        {values.annualPuja && (
                            <Grid item>
                                <FormikTextField name='occurrenceCount' label='Occurrence Count' type='number' variant='outlined' />
                            </Grid>
                        )}
                        <Grid item>
                            <FormControl variant='outlined' fullWidth>
                                <InputLabel id='staff-assignment-status-label'>Status</InputLabel>
                                <Select
                                    labelId='staff-assignment-status-label'
                                    label='Status'
                                    value={values.status}
                                    onChange={event => setFieldValue('status', event.target.value)}
                                >
                                    <MenuItem value='scheduled'>Scheduled</MenuItem>
                                    <MenuItem value='cancelled'>Cancelled</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item>
                            <FormikTextField name='notes' label='Notes' placeholder='Internal notes' variant='outlined' multiline rows={4} />
                        </Grid>
                        <Grid item>
                            <Button
                                color='primary'
                                variant='contained'
                                type='submit'
                                disabled={isSubmitting || !dirty || !isValid || api.status === apiStatus.LOADING}
                            >
                                {buttonLabel}
                            </Button>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </Formik>
    )
}

export default FormStaffAssignment
